import React, { useState } from 'react';
import { useFarmStore } from '@/store/useFarmStore';
import { Navbar } from '@/components/layout/Navbar';
import { PackageCard } from '@/components/farm/PackageCard';
import { AdvancedROIProjector } from '@/components/farm/AdvancedROIProjector';
import { Button } from '@/components/ui/button';
import { Link, Navigate } from 'react-router-dom';
import { RefreshCcw, Info, Download, Grid3x3, LayoutList, ChevronDown, ChevronRight, Leaf, Fish, Pill, Wrench, GraduationCap, HelpCircle, TrendingUp, DollarSign, Calendar, MapPin, ThumbsUp, ThumbsDown, Meh } from 'lucide-react';
import { useCurrency } from '@/hooks/use-currency';
import { PriorityLevel, PackageItem } from '@shared/types';
import { api } from '@/lib/api-client';

type FeedbackType = 'liked' | 'disliked' | 'neutral';

interface CategorySectionProps {
  title: string;
  icon: React.ReactNode;
  items: PackageItem[];
  defaultOpen?: boolean;
}

function CategorySection({ title, icon, items, defaultOpen = false }: CategorySectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { formatFromBase } = useCurrency();

  const priorityColors: Record<PriorityLevel, string> = {
    essential: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    recommended: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    optional: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  };

  const priorityLabels: Record<PriorityLevel, string> = {
    essential: 'Essential',
    recommended: 'Recommended',
    optional: 'Optional',
  };

  const whyThisMatters: Record<string, string> = {
    essential: 'Critical for operation - without this your farm cannot function',
    recommended: 'Significantly improves success rates and reduces risks',
    optional: 'Enhances efficiency but not required to start',
  };

  if (items.length === 0) return null;

  return (
    <div className="border rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-primary/10 text-primary">{icon}</div>
          <div>
            <span className="font-medium text-sm">{title}</span>
            <span className="text-xs text-muted-foreground ml-2">({items.length} items)</span>
          </div>
        </div>
        {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>
      {isOpen && (
        <div className="border-t divide-y">
          {items.map((item, idx) => {
            const priority = item.priority || 'recommended';
            return (
              <div key={idx} className="p-4 flex items-start gap-4 hover:bg-muted/20 transition-colors">
                <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                  {item.product.subcategory === 'animal' ? <Fish className="h-5 w-5 text-muted-foreground" /> :
                   item.product.subcategory === 'feed' ? <Leaf className="h-5 w-5 text-muted-foreground" /> :
                   item.product.subcategory === 'medication' ? <Pill className="h-5 w-5 text-muted-foreground" /> :
                   item.product.subcategory === 'equipment' ? <Wrench className="h-5 w-5 text-muted-foreground" /> :
                   <GraduationCap className="h-5 w-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{item.product.name}</span>
                    {item.priority === 'essential' && (
                      <div className="group relative">
                        <HelpCircle className="h-3.5 w-3.5 text-red-500 cursor-help" />
                        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-foreground text-background text-xs rounded-md w-48 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-opacity">
                          <span className="font-medium">Why this matters:</span>
                          <p className="mt-1 opacity-90">{whyThisMatters[priority]}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>Qty: {item.quantity} {item.product.unit}</span>
                    <span>× {formatFromBase(item.product.price)} each</span>
                    <span className="font-medium">{formatFromBase(item.subtotal)}</span>
                  </div>
                </div>
                <div className={`px-2 py-0.5 rounded text-[10px] font-medium ${priorityColors[priority]}`}>
                  {priorityLabels[priority]}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ResultsPage() {
  const recommendations = useFarmStore((s) => s.recommendations);
  const preferences = useFarmStore((s) => s.preferences);
  const sessionId = useFarmStore((s) => s.recommendationSessionId);
  const [selectedPkgId, setSelectedPkgId] = useState<string>(
    recommendations.find(r => r.type === 'recommended')?.id || recommendations[0]?.id || ''
  );
  const [viewMode, setViewMode] = useState<'detailed' | 'comparison'>('detailed');
  const [feedbackState, setFeedbackState] = useState<Record<string, FeedbackType>>({});
  const { formatFromBase } = useCurrency();

  const handleFeedback = async (packageId: string, feedback: FeedbackType) => {
    if (!sessionId) return;
    setFeedbackState(prev => ({ ...prev, [packageId]: feedback }));
    try {
      await api('/api/recommendations/feedback', {
        method: 'POST',
        body: JSON.stringify({ sessionId, packageId, feedback }),
      });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  if (recommendations.length === 0) {
    return <Navigate to="/wizard" />;
  }
  const activePackage = recommendations.find(r => r.id === selectedPkgId) || recommendations[0];

  const categorizeItems = () => {
    const categories = {
      livestock: [] as PackageItem[],
      feed: [] as PackageItem[],
      equipment: [] as PackageItem[],
      medication: [] as PackageItem[],
      services: [] as PackageItem[],
    };
    if (!activePackage) return categories;
    
    activePackage.items.forEach(item => {
      const priority: PriorityLevel = item.product.subcategory === 'animal' ? 'essential' :
                                  item.product.subcategory === 'feed' ? 'essential' :
                                  item.product.subcategory === 'medication' ? 'recommended' :
                                  item.product.subcategory === 'equipment' ? 'recommended' :
                                  item.product.subcategory === 'training' ? 'optional' : 'recommended';
      const itemWithPriority = { ...item, priority };
      switch (item.product.subcategory) {
        case 'animal':
          categories.livestock.push(itemWithPriority);
          break;
        case 'feed':
          categories.feed.push(itemWithPriority);
          break;
        case 'equipment':
        case 'housing':
          categories.equipment.push(itemWithPriority);
          break;
        case 'medication':
          categories.medication.push(itemWithPriority);
          break;
        case 'training':
          categories.services.push(itemWithPriority);
          break;
        default:
          categories.equipment.push(itemWithPriority);
      }
    });
    return categories;
  };

  const categories = categorizeItems();
  const harvestMonths = activePackage?.breakEvenMonth || 6;
  const estimatedYield = activePackage?.estimatedYield || Math.floor(activePackage?.totalCost ? activePackage.totalCost * 0.8 : 0);
  const expectedRevenue = activePackage?.expectedRevenue || activePackage?.projectedAnnualRevenue || 0;
  const yieldUnit = activePackage?.yieldUnit || 'kg';

  const exportScenario = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      farmType: preferences.livestockType,
      preferences: {
        experience: preferences.experience,
        budget: preferences.budget,
        spaceSqM: preferences.spaceSqM,
        location: preferences.location,
        goal: preferences.goal,
        timeAvailability: preferences.timeAvailability,
        waterSource: preferences.waterSource,
        trainingNeeded: preferences.trainingNeeded,
      },
      selectedPackage: {
        type: activePackage.type,
        name: activePackage.name,
        totalCost: activePackage.totalCost,
        deliveryFee: activePackage.deliveryFee,
        projectedAnnualRevenue: activePackage.projectedAnnualRevenue,
        projectedAnnualProfit: activePackage.projectedAnnualProfit,
        roiPercentage: activePackage.roiPercentage,
        confidenceScore: activePackage.confidenceScore,
        breakEvenMonth: activePackage.breakEvenMonth,
        description: activePackage.description,
        explanationText: activePackage.explanationText,
        items: activePackage.items.map(item => ({
          productName: item.product.name,
          productCategory: item.product.category,
          productSubcategory: item.product.subcategory,
          quantity: item.quantity,
          price: item.product.price,
          subtotal: item.subtotal,
          frequency: item.frequency,
        })),
      },
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `farmpack-scenario-${preferences.livestockType}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return (
    <div className="min-h-screen bg-muted/20">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 space-y-12">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Your Intelligent Farm Plan</h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Review your tailored options for {preferences.livestockType} farming.
            Select a package to see detailed ROI projections.
          </p>
          <div className="flex justify-center gap-2 pt-2">
            <Button
              variant={viewMode === 'detailed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('detailed')}
            >
              <LayoutList className="mr-2 h-3 w-3" /> Detailed View
            </Button>
            <Button
              variant={viewMode === 'comparison' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('comparison')}
            >
              <Grid3x3 className="mr-2 h-3 w-3" /> Compare All
            </Button>
          </div>
          <div className="flex justify-center gap-4 pt-4 text-xs text-muted-foreground">
            {preferences.region && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {preferences.region}
              </span>
            )}
            {preferences.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {preferences.location}
              </span>
            )}
            {preferences.hasElectricity !== undefined && (
              <span className={`flex items-center gap-1 ${preferences.hasElectricity ? 'text-green-600' : 'text-red-500'}`}>
                {preferences.hasElectricity ? '⚡ Power' : '⚡ No power'}
              </span>
            )}
            {preferences.hasWaterSource !== undefined && (
              <span className={`flex items-center gap-1 ${preferences.hasWaterSource ? 'text-blue-600' : 'text-red-500'}`}>
                {preferences.hasWaterSource ? '💧 Water' : '💧 No water'}
              </span>
            )}
          </div>
          {preferences.spacePhoto && (
            <div className="flex justify-center pt-2">
              <img 
                src={preferences.spacePhoto} 
                alt="Your farm space" 
                className="w-32 h-24 object-cover rounded-md border shadow-sm"
              />
            </div>
          )}
        </div>
        {viewMode === 'detailed' ? (
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 xl:col-span-4 space-y-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2 px-1">
              <Info className="h-3 w-3" /> Select Package
            </h2>
            <div className="space-y-4">
              {recommendations.map((pkg) => (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPkgId(pkg.id)}
                  className="cursor-pointer"
                >
                  <PackageCard pkg={pkg} />
                  {sessionId && (
                    <div className="flex items-center justify-center gap-2 mt-2 pb-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFeedback(pkg.id, 'liked'); }}
                        className={`p-1.5 rounded-md transition-colors ${feedbackState[pkg.id] === 'liked' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' : 'hover:bg-muted text-muted-foreground'}`}
                        title="Like this package"
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFeedback(pkg.id, 'neutral'); }}
                        className={`p-1.5 rounded-md transition-colors ${feedbackState[pkg.id] === 'neutral' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' : 'hover:bg-muted text-muted-foreground'}`}
                        title="Neutral"
                      >
                        <Meh className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleFeedback(pkg.id, 'disliked'); }}
                        className={`p-1.5 rounded-md transition-colors ${feedbackState[pkg.id] === 'disliked' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' : 'hover:bg-muted text-muted-foreground'}`}
                        title="Dislike this package"
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 xl:col-span-8 space-y-8">
            <div className="bg-background rounded-md p-6 md:p-8 border shadow-sm space-y-8">
              <div className="space-y-1">
                <h2 className="text-xl font-bold">{activePackage.name} Analysis</h2>
                <p className="text-xs text-muted-foreground">Itemized investment and 12-month profitability forecast.</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-4 space-y-2">
                <p className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase tracking-widest">Why this package?</p>
                <p className="text-sm text-blue-900 dark:text-blue-200 leading-relaxed">{activePackage.explanationText}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-muted/30 rounded-md p-4 text-center">
                  <Calendar className="h-5 w-5 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{harvestMonths}</p>
                  <p className="text-xs text-muted-foreground uppercase">Harvest Ready (months)</p>
                </div>
                <div className="bg-muted/30 rounded-md p-4 text-center">
                  <TrendingUp className="h-5 w-5 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold">{estimatedYield.toLocaleString()}{yieldUnit}</p>
                  <p className="text-xs text-muted-foreground uppercase">Est. Yield</p>
                </div>
                <div className="bg-muted/30 rounded-md p-4 text-center">
                  <DollarSign className="h-5 w-5 mx-auto mb-2 text-amber-600" />
                  <p className="text-2xl font-bold">{formatFromBase(expectedRevenue)}</p>
                  <p className="text-xs text-muted-foreground uppercase">Expected Revenue</p>
                </div>
              </div>
              <div key={selectedPkgId}>
                <AdvancedROIProjector pkg={activePackage} />
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Package Contents</h3>
                <CategorySection
                  title="Livestock"
                  icon={<Fish className="h-4 w-4" />}
                  items={categories.livestock}
                  defaultOpen={true}
                />
                <CategorySection
                  title="Feed"
                  icon={<Leaf className="h-4 w-4" />}
                  items={categories.feed}
                />
                <CategorySection
                  title="Equipment & Housing"
                  icon={<Wrench className="h-4 w-4" />}
                  items={categories.equipment}
                />
                <CategorySection
                  title="Medication & Health"
                  icon={<Pill className="h-4 w-4" />}
                  items={categories.medication}
                />
                <CategorySection
                  title="Services & Training"
                  icon={<GraduationCap className="h-4 w-4" />}
                  items={categories.services}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="outline" size="sm" asChild>
                <Link to="/wizard">
                  <RefreshCcw className="mr-2 h-3 w-3" /> Refine Preferences
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={exportScenario}>
                <Download className="mr-2 h-3 w-3" /> Export Scenario
              </Button>
            </div>
          </div>
        </div>
        ) : (
          <div className="bg-background rounded-md border shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground">Metric</th>
                  {recommendations.map(pkg => (
                    <th key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/10' : ''}`}>
                      <div className="font-bold text-sm">{pkg.name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{pkg.type}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-4 font-medium">Total Cost</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {formatFromBase(pkg.totalCost)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">ROI</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      <span className="font-bold text-green-600">{pkg.roiPercentage}%</span>
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Confidence Score</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {pkg.confidenceScore}%
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Break-Even</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {pkg.breakEvenMonth} months
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Annual Revenue</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {formatFromBase(pkg.projectedAnnualRevenue)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Annual Profit</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {formatFromBase(pkg.projectedAnnualProfit)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="p-4 font-medium">Item Count</td>
                  {recommendations.map(pkg => (
                    <td key={pkg.id} className={`p-4 text-center ${pkg.type === 'recommended' ? 'bg-primary/5' : ''}`}>
                      {pkg.items.length} items
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <div className="p-6 bg-muted/20 border-t text-center space-y-4">
              <p className="text-xs text-muted-foreground">Select a package above, then switch to Detailed View for full analysis.</p>
              <div className="flex flex-wrap justify-center gap-3">
                {recommendations.map(pkg => (
                  <Button
                    key={pkg.id}
                    size="sm"
                    variant={pkg.type === 'recommended' ? 'default' : 'outline'}
                    onClick={() => { setSelectedPkgId(pkg.id); setViewMode('detailed'); }}
                  >
                    View {pkg.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}